import { useSidebarStore } from './sidebar-store';

// Only the AMC plugin host floats the overlay hamburger (app.tsx HamburgerButton).
// The standalone web build and the pinned-sidebar (Regular View) layout never
// show it, so there is nothing to clear there.
const IS_AMC_PLUGIN = import.meta.env.VITE_BUILD_TARGET === 'amc-plugin';

/**
 * Reserves the top-left slot the floating overlay hamburger sits in (app.tsx
 * `OpenNavButton`, `absolute left-6 top-2.5`). In AMC overlay mode — i.e. Full
 * View, where the persistent sidebar is hidden and the hamburger is the only nav
 * affordance — the hamburger is absolutely positioned over the header's top-left
 * corner. Without this spacer it overlaps the header's title / tabs / actions and
 * swallows clicks meant for them (which reads as the app "freezing").
 *
 * Every full-width page header renders this as its FIRST child so the fix stays
 * consistent across folders (Inbox / Done / Sent / Snoozed / Follow-ups / …) and
 * the other top-level pages. Renders nothing when the sidebar is pinned (Regular
 * View) or outside the AMC plugin host, so those layouts are unaffected.
 *
 * The geometry is shared with OpenNavButton on purpose: every header is `h-12 px-6`,
 * so this `w-7` (28px) spacer occupies x = 24–52px — exactly the button's own `left-6`
 * + `w-7` box — and `top-2.5` (10px) centres the 28px button in the 48px row. `mr-2`
 * then spaces the header's first item off the button like any other toolbar gap, so
 * the button lines up with the header content instead of floating apart from it.
 */
export function HamburgerGutter() {
  const sidebarHidden = useSidebarStore((s) => s.hidden);
  if (!IS_AMC_PLUGIN || !sidebarHidden) return null;
  return <div className="w-7 mr-2 shrink-0" aria-hidden="true" data-testid="overlay-hamburger-gutter" />;
}
